/**
 * Personnel Costs Section
 * Extracted from FinancePage.jsx
 */

import React from 'react';
import { ArrowLeft, TrendingUp, TrendingDown, Users, Shield, GraduationCap, Target, ArrowUpRight, ArrowDownRight, Minus, Info } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

const monthTranslations = {
  'Jan': { en: 'Jan', pt: 'Jan' },
  'Feb': { en: 'Feb', pt: 'Fev' },
  'Mar': { en: 'Mar', pt: 'Mar' },
  'Apr': { en: 'Apr', pt: 'Abr' },
  'May': { en: 'May', pt: 'Mai' },
  'Jun': { en: 'Jun', pt: 'Jun' },
  'Jul': { en: 'Jul', pt: 'Jul' },
  'Aug': { en: 'Aug', pt: 'Ago' },
  'Sep': { en: 'Sep', pt: 'Set' },
  'Oct': { en: 'Oct', pt: 'Out' },
  'Nov': { en: 'Nov', pt: 'Nov' },
  'Dec': { en: 'Dec', pt: 'Dez' }
};

const translateMonth = (month, language) => {
  return monthTranslations[month]?.[language] || month;
};

const PersonnelCosts = ({ year, language, setActiveSection, flowMap }) => {
  const [costAreaCompareMode, setCostAreaCompareMode] = React.useState('yoy');
  const [costQualityCompareMode, setCostQualityCompareMode] = React.useState('yoy');
  
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  
  const personnelDataByYear = mockData?.personnelCosts || {};
  const personnelData = personnelDataByYear[year] || personnelDataByYear[2025] || {
    totalCost: 0, weightInOpex: 0, yoyChange: 0, vsBudget: 0, headcount: 0, avgCostPerEmployee: 0,
    productionPerEmployee: 0, overtimeRatio: 0, headcountByFunction: [], costByArea: [], costComposition: [],
    costPerUnit: { perTonMoved: 0, perTonProcessed: 0, perCaratProduced: 0 },
    fixedVsVariable: { fixed: { value: 0, percentage: 0 }, variable: { value: 0, percentage: 0 } },
    directVsIndirect: { direct: { count: 0, percentage: 0 }, indirect: { count: 0, percentage: 0 } },
    monthlyData: [], yoyWaterfall: []
  };
  const prevPersonnelData = personnelDataByYear[year - 1] || personnelDataByYear[2024] || personnelData;

  // Budget data for personnel costs
  const budgetPersonnelCost = personnelData.totalCost * 0.97; // Budget typically 3% lower
  const personnelVsBudget = ((personnelData.totalCost - budgetPersonnelCost) / budgetPersonnelCost * 100).toFixed(1);
  const personnelVsBudgetAbs = (personnelData.totalCost - budgetPersonnelCost).toFixed(1);
  const budgetHeadcount = Math.round(personnelData.headcount * 0.98);
  const headcountVsBudget = personnelData.headcount - budgetHeadcount;

  // Calculate Personnel cost per carat (regular carats recovered)
  const costPerCaratData = mockData.costPerCarat?.[year] || {};
  const prevCostPerCaratData = mockData.costPerCarat?.[year - 1] || {};
  const regularCarats = costPerCaratData.kpis?.regularCarats?.value || 920;
  const prevRegularCarats = prevCostPerCaratData.kpis?.regularCarats?.value || 820;
  
  const personnelPerCarat = regularCarats > 0 ? Math.round((personnelData.totalCost * 1000) / regularCarats) : 0;
  const prevPersonnelPerCarat = prevRegularCarats > 0 ? Math.round((prevPersonnelData.totalCost * 1000) / prevRegularCarats) : 0;
  const personnelPerCaratChange = prevPersonnelPerCarat > 0 ? (personnelPerCarat - prevPersonnelPerCarat) : 0;

  return (
    <div className="space-y-6" data-testid="personnel-costs-section">
      {/* Header */}
      <div>
        <button
          onClick={() => setActiveSection('opex-detail')}
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: '#7A859D' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar a Custos Operacionais' : 'Back to Operating Costs'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Custos com Pessoal' : 'Personnel Costs'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Insights'}
          </h3>
        </div>
        
        <div className="grid grid-cols-3 gap-5">
          {/* Growth Card */}
          <div className={`${parseFloat(personnelVsBudget) > 0 ? 'bg-red-500/20 border-red-500/30' : 'bg-emerald-500/20 border-emerald-500/30'} border rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${parseFloat(personnelVsBudget) > 0 ? 'bg-red-500/30' : 'bg-emerald-500/30'}`}>
                <TrendingUp className={`w-5 h-5 ${parseFloat(personnelVsBudget) > 0 ? 'text-red-400' : 'text-emerald-400'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-white text-sm mb-1.5 leading-tight">
                  {language === 'pt' ? 'vs Orçamento' : 'vs Budget'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `${personnelVsBudget > 0 ? '+' : ''}${personnelVsBudget}% Orçamento 2025 (+${personnelVsBudgetAbs}M USD). YoY: +${personnelData.yoyChange}%, de $${prevPersonnelData?.totalCost || 54.2}M para $${personnelData.totalCost}M.`
                    : `${personnelVsBudget > 0 ? '+' : ''}${personnelVsBudget}% Budget 2025 (+${personnelVsBudgetAbs}M USD). YoY: +${personnelData.yoyChange}%, from $${prevPersonnelData?.totalCost || 54.2}M to $${personnelData.totalCost}M.`}
                </p>
              </div>
            </div>
          </div>

          {/* Productivity Card */}
          <div className={`${personnelData.productionPerEmployee > (prevPersonnelData?.productionPerEmployee || 0) ? 'bg-emerald-500/20 border-emerald-500/30' : 'bg-amber-500/20 border-amber-500/30'} border rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${personnelData.productionPerEmployee > (prevPersonnelData?.productionPerEmployee || 0) ? 'bg-emerald-500/30' : 'bg-amber-500/30'}`}>
                {personnelData.productionPerEmployee > (prevPersonnelData?.productionPerEmployee || 0) ? (
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-amber-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-white text-sm mb-1.5 leading-tight">
                  {language === 'pt' ? 'Produtividade' : 'Productivity'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `${personnelData.productionPerEmployee} cts/Colab. (${personnelData.productionPerEmployee > (prevPersonnelData?.productionPerEmployee || 0) ? '+' : ''}${personnelData.productionPerEmployee - (prevPersonnelData?.productionPerEmployee || 597)} vs LY). Eficiência operacional ${personnelData.productionPerEmployee > (prevPersonnelData?.productionPerEmployee || 0) ? 'em melhoria' : 'sob pressão'}.`
                    : `${personnelData.productionPerEmployee} cts/Emp. (${personnelData.productionPerEmployee > (prevPersonnelData?.productionPerEmployee || 0) ? '+' : ''}${personnelData.productionPerEmployee - (prevPersonnelData?.productionPerEmployee || 597)} vs LY). Operational efficiency ${personnelData.productionPerEmployee > (prevPersonnelData?.productionPerEmployee || 0) ? 'improving' : 'under pressure'}.`}
                </p>
              </div>
            </div>
          </div>

          {/* Structure Card */}
          <div className="bg-blue-500/20 border border-blue-500/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-500/30">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-white text-sm mb-1.5 leading-tight">
                  {language === 'pt' ? 'Estrutura' : 'Structure'}
                </h4>
                <p className="text-slate-400 text-xs leading-relaxed">
                  {language === 'pt' 
                    ? `${personnelData.directVsIndirect.direct.percentage}% directo vs ${personnelData.directVsIndirect.indirect.percentage}% indirecto. Overtime ${personnelData.overtimeRatio}%. Custo/quilate $${personnelData.costPerUnit.perCaratProduced}.`
                    : `${personnelData.directVsIndirect.direct.percentage}% direct vs ${personnelData.directVsIndirect.indirect.percentage}% indirect. Overtime ${personnelData.overtimeRatio}%. Cost/carat $${personnelData.costPerUnit.perCaratProduced}.`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main KPIs - 5 cards */}
      <div className="grid grid-cols-5 gap-4">
        {/* KPI 1 - Custos com Pessoal */}
        {(() => {
          const numChange = personnelData.yoyChange;
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-red-50';
          const trendColor = isPositive ? '#10B981' : '#EF4444';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-4 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? 'Custos com Pessoal' : 'Personnel Costs'}</p>
              <p className="font-serif text-2xl font-bold text-primary">{personnelData.totalCost}<span className="text-sm text-slate-400 ml-1">M USD</span></p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold ${parseFloat(personnelVsBudget) > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {parseFloat(personnelVsBudget) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {personnelVsBudget > 0 ? '+' : ''}{personnelVsBudget}% Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold ${personnelData.yoyChange > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {personnelData.yoyChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {personnelData.yoyChange > 0 ? '+' : ''}{personnelData.yoyChange}% YoY
                </span>
              </div>
            </div>
          );
        })()}
        
        {/* KPI 2 - Peso nos Custos Operacionais */}
        {(() => {
          const numChange = personnelData.weightInOpex - (prevPersonnelData?.weightInOpex || 0);
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-red-50';
          const trendColor = isPositive ? '#10B981' : '#EF4444';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-4 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? 'Peso nos Custos Operacionais' : 'Weight in Operating Costs'}</p>
              <p className="font-serif text-2xl font-bold text-primary">{personnelData.weightInOpex}<span className="text-sm text-slate-400 ml-1">%</span></p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold ${personnelData.weightInOpex > 32.5 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {personnelData.weightInOpex > 32.5 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  +0.7p.p. vs Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold ${personnelData.weightInOpex > prevPersonnelData?.weightInOpex ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {personnelData.weightInOpex > prevPersonnelData?.weightInOpex ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {personnelData.weightInOpex > prevPersonnelData?.weightInOpex ? '+' : ''}{(personnelData.weightInOpex - (prevPersonnelData?.weightInOpex || 0)).toFixed(1)}p.p. YoY
                </span>
              </div>
            </div>
          );
        })()}
        
        {/* KPI 3 - Colaboradores */}
        {(() => {
          const numChange = personnelData.headcount - (prevPersonnelData?.headcount || 0);
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-amber-50';
          const trendColor = isPositive ? '#10B981' : '#F59E0B';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-4 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? 'Colaboradores' : 'Headcount'}</p>
              <p className="font-serif text-2xl font-bold text-primary">{personnelData.headcount.toLocaleString()}</p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold ${headcountVsBudget > 0 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {headcountVsBudget > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {headcountVsBudget > 0 ? '+' : ''}{headcountVsBudget} vs Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold ${personnelData.headcount > prevPersonnelData?.headcount ? 'bg-amber-50 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {personnelData.headcount > prevPersonnelData?.headcount ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {personnelData.headcount > prevPersonnelData?.headcount ? '+' : ''}{personnelData.headcount - (prevPersonnelData?.headcount || 0)} YoY
                </span>
              </div>
            </div>
          );
        })()}
        
        {/* KPI 4 - Custo Médio/Colaborador */}
        {(() => {
          const numChange = personnelData.avgCostPerEmployee - (prevPersonnelData?.avgCostPerEmployee || 0);
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-red-50';
          const trendColor = isPositive ? '#10B981' : '#EF4444';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-4 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? 'Custo Médio/Colaborador' : 'Avg Cost/Employee'}</p>
              <p className="font-serif text-2xl font-bold text-primary">{personnelData.avgCostPerEmployee}<span className="text-sm text-slate-400 ml-1">k USD</span></p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700`}>
                  <TrendingUp className="w-3 h-3" />
                  +2.4% Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold ${personnelData.avgCostPerEmployee > prevPersonnelData?.avgCostPerEmployee ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {personnelData.avgCostPerEmployee > prevPersonnelData?.avgCostPerEmployee ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  +{(personnelData.avgCostPerEmployee - (prevPersonnelData?.avgCostPerEmployee || 0)).toFixed(1)}k YoY
                </span>
              </div>
            </div>
          );
        })()}
        
        {/* KPI 5 - Custo com Pessoal/Quilate Recuperado (Pedras Regulares) */}
        {(() => {
          const numChange = personnelPerCaratChange;
          const isPositive = numChange <= 0;
          const trendBg = isPositive ? 'bg-emerald-50' : 'bg-red-50';
          const trendColor = isPositive ? '#10B981' : '#EF4444';
          const TrendIcon = numChange > 0 ? ArrowUpRight : numChange < 0 ? ArrowDownRight : Minus;
          return (
            <div className="bg-white rounded-xl border border-slate-200 p-4 relative">
              <div className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${trendBg}`}>
                <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
              </div>
              <p className="text-xs text-slate-500 tracking-wider mb-1">{language === 'pt' ? 'Custo com Pessoal/Quilate Recuperado (Pedras Regulares)' : 'Personnel Cost/Recovered Carat (Regular Stones)'}</p>
              <p className="font-serif text-2xl font-bold text-primary">{personnelPerCarat}<span className="text-sm text-slate-400 ml-1">USD</span></p>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700`}>
                  <TrendingUp className="w-3 h-3" />
                  +4.2% Orç.
                </span>
                <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-semibold ${personnelPerCaratChange > 0 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {personnelPerCaratChange > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {personnelPerCaratChange > 0 ? '+' : ''}{personnelPerCaratChange} USD YoY
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Sumário Executivo - Executive Summary - Expanded with Driver Analysis */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Custos com pessoal e estrutura organizacional' : 'Personnel costs and organizational structure'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="personnel-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Em 2025, os custos com pessoal totalizaram USD ${personnelData.totalCost.toFixed(1)}M, ficando ${personnelVsBudget}% acima do orçamento e ${personnelData.yoyChange.toFixed(1)}% acima de 2024. O desvio face ao orçamento resulta de uma combinação de headcount acima do previsto (${personnelData.headcount.toLocaleString()} colaboradores; +39 vs Orç.) e custo médio por colaborador acima do plano (USD ${(personnelData.totalCost / personnelData.headcount * 1000).toFixed(1)}k; +2,4% Orç.). Face a 2024, o aumento é sobretudo explicado por escala de estrutura (+114 colaboradores) e, em menor grau, por maior custo médio por colaborador. O ponto central desta secção não é o crescimento nominal da rubrica, mas a forma como esse crescimento se distribuiu entre massa salarial, encargos e benefícios, e se foi absorvido pelos indicadores operacionais.`
              : `In 2025, personnel costs totaled USD ${personnelData.totalCost.toFixed(1)}M, ${personnelVsBudget}% above budget and ${personnelData.yoyChange.toFixed(1)}% above 2024. The budget deviation results from a combination of headcount above plan (${personnelData.headcount.toLocaleString()} employees; +39 vs Budget) and average cost per employee above plan. The 2024 increase is mainly explained by structure scale (+114 employees) and, to a lesser extent, higher average cost per employee.`}
          </p>
        </div>

        {/* Driver Sections */}
        <div className="space-y-4">
          {/* 1. Remuneração e encargos */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Remuneração e Encargos' : 'Remuneration & Charges'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Principal driver do desvio' : 'Main deviation driver'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">50.6</span>
                <span className="text-xs text-slate-500">M USD (77.8%)</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'A maior parte do custo continua concentrada em Remunerações – Pessoal (USD 39,0M; 60,0% do total), seguida de Encargos sobre remunerações (USD 7,8M; 12,0%) e Remunerações – Órgãos Sociais (USD 3,8M; 5,8%). Em conjunto, estas três linhas representam 77,8% do custo total com pessoal.'
                : 'Most of the cost remains concentrated in Personnel Remuneration (USD 39.0M; 60.0%), followed by Social Charges (USD 7.8M; 12.0%) and Executive Remuneration (USD 3.8M; 5.8%). Together, these three lines represent 77.8% of total personnel costs.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Face ao orçamento, o desvio desta base salarial é coerente com o aumento de colaboradores acima do plano. Face ao período homólogo, a variação reflete sobretudo maior headcount e não uma alteração material na estrutura de custo unitário.'
                : 'Against budget, the deviation is consistent with headcount above plan. Against prior year, the variation mainly reflects higher headcount rather than material changes in unit cost structure.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Leitura de Gestão' : 'Management Reading'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'O desvio vs orçamento é explicado sobretudo por mais colaboradores do que o previsto' : 'Budget deviation mainly explained by more employees than planned'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'O aumento vs 2024 foi impulsionado principalmente por massa salarial adicional' : '2024 increase mainly driven by additional payroll mass'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? 'A principal variável de controlo continua a ser headcount efectivo por área e função' : 'Main control variable remains effective headcount by area and function'}
                </li>
              </ul>
            </div>
          </div>

          {/* 2. Benefícios e protecção */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Benefícios e Protecção' : 'Benefits & Protection'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Crescimento em linha com expansão' : 'Growth aligned with expansion'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">10.4</span>
                <span className="text-xs text-slate-500">M USD (16.0%)</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'As linhas de Seguro de saúde (USD 4,5M; 6,9% do total), Seguros de acidente de trabalho (USD 2,6M; 4,0%) e Bens alimentares e refeições (USD 3,3M; 5,1%) totalizam USD 10,4M, equivalentes a 16,0% do custo com pessoal.'
                : 'Health Insurance (USD 4.5M; 6.9%), Work Accident Insurance (USD 2.6M; 4.0%) and Food & Meals (USD 3.3M; 5.1%) total USD 10.4M, equivalent to 16.0% of personnel costs.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Face ao período homólogo, o crescimento foi superior a 18% em todas as rubricas e acima de 22% em alimentação e refeições, o que sugere que o aumento do quadro teve tradução directa nos custos de suporte ao colaborador. O driver é predominantemente quantitativo: mais pessoas, maior base segurável e maior custo de apoio recorrente.'
                : 'Year-over-year growth exceeded 18% in all categories and above 22% in food & meals, suggesting headcount increase directly translated into employee support costs. The driver is predominantly quantitative: more people, larger insurable base and higher recurring support cost.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Leitura de Gestão' : 'Management Reading'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'O crescimento acompanha aumento do quadro e maior custo de suporte por colaborador' : 'Growth follows headcount increase and higher support cost per employee'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'Alimentação e apólices são as linhas onde a expansão do efectivo se torna mais visível' : 'Food and insurance are lines where headcount expansion becomes most visible'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? 'O controlo passa por densidade de efectivos, regime de benefícios e disciplina de elegibilidade' : 'Control through headcount density, benefits regime and eligibility discipline'}
                </li>
              </ul>
            </div>
          </div>

          {/* 3. Formação e outras despesas */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Formação e Outras Despesas' : 'Training & Other Expenses'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Relevante em leitura, não em valor' : 'Relevant in reading, not value'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">4.0</span>
                <span className="text-xs text-slate-500">M USD (6.2%)</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Formação e Outras despesas com pessoal representam, em conjunto, USD 4,0M (6,2% do total). Em termos absolutos, não explicam o desvio principal da rubrica, mas em termos relativos merecem atenção: Formação cresceu 25,0% vs 2024, o maior aumento percentual da decomposição.'
                : 'Training and Other personnel expenses together represent USD 4.0M (6.2% of total). In absolute terms, they don\'t explain the main deviation, but in relative terms deserve attention: Training grew 25.0% vs 2024, the highest percentage increase in the breakdown.'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'Face ao período homólogo, o aumento sugere maior esforço de integração, capacitação e suporte ao crescimento da estrutura. Esta componente ajuda a interpretar se a expansão do quadro foi acompanhada por investimento em integração e qualificação.'
                : 'Year-over-year, the increase suggests greater integration, training and structure growth support effort. This component helps interpret whether headcount expansion was accompanied by integration and qualification investment.'}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-amber-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Leitura de Gestão' : 'Management Reading'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Não é a rubrica que explica o desvio principal, mas ajuda a interpretar a qualidade da expansão' : 'Not the main deviation driver, but helps interpret expansion quality'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'Formação acima do homólogo pode ser consistente com integração de novos efectivos' : 'Training above prior year may be consistent with new employee integration'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  {language === 'pt' ? 'O foco deve estar em confirmar se o acréscimo teve correspondência em áreas produtivas' : 'Focus should confirm if increase corresponded to productive areas'}
                </li>
              </ul>
            </div>
          </div>

          {/* 4. Estrutura, áreas e produtividade */}
          <div className="bg-gradient-to-r from-purple-50 to-slate-50 rounded-xl p-5 border border-purple-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Estrutura, Áreas e Produtividade' : 'Structure, Areas & Productivity'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Onde o custo foi absorvido' : 'Where cost was absorbed'}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-lg font-bold text-primary">{personnelData.productionPerEmployee}</span>
                <span className="text-xs text-slate-500">{language === 'pt' ? 'cts/Colab.' : 'cts/Emp.'}</span>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? 'A distribuição do custo por área mostra concentração em Exploração (USD 27,3M; 42% do total), seguida de Metalurgia (USD 11,1M; 17,1%), Geologia (USD 8,0M; 12,3%) e Perfuração e Explosivos (USD 7,1M; 10,9%). O crescimento relativo mais acentuado ocorreu em Suporte (Mina) (+94%) e Suporte (Luanda) (+51%).'
                : 'Cost distribution by area shows concentration in Exploration (USD 27.3M; 42%), followed by Metallurgy (USD 11.1M; 17.1%), Geology (USD 8.0M; 12.3%) and Drilling & Explosives (USD 7.1M; 10.9%). Highest relative growth occurred in Support (Mine) (+94%) and Support (Luanda) (+51%).'}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Em termos de absorção: produtividade em ${personnelData.productionPerEmployee} cts/Colaborador, acima do ano anterior; custo por unidade operacional também acima de 2024, com USD ${personnelData.costPerUnit.perTonMoved}/m³ movimentado, USD ${personnelData.costPerUnit.perTonProcessed}/m³ tratado e USD ${personnelData.costPerUnit.perCaratProduced}/ct produzido. O aumento da estrutura foi acompanhado por maior produção por colaborador, mas não foi integralmente diluído nos indicadores unitários.`
                : `In terms of absorption: productivity at ${personnelData.productionPerEmployee} cts/Employee, above prior year; cost per operational unit also above 2024. Structure increase was accompanied by higher production per employee, but was not fully diluted in unit indicators.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-purple-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Leitura de Gestão' : 'Management Reading'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  {language === 'pt' ? 'O aumento do custo concentrou-se sobretudo em áreas operacionais, mas houve expansão em suporte' : 'Cost increase concentrated in operational areas, but expansion in support too'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  {language === 'pt' ? 'A produtividade melhorou, mas a diluição do custo por unidade foi apenas parcial' : 'Productivity improved, but unit cost dilution was only partial'}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                  {language === 'pt' ? 'O tema central é o equilíbrio entre escala de estrutura, produtividade e custo unitário' : 'Central theme is balance between structure scale, productivity and unit cost'}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Fecho Executivo */}
        <div className="mt-6 pt-6 border-t border-slate-200">
          <h4 className="font-semibold text-primary text-sm mb-3">{language === 'pt' ? 'Fecho Executivo' : 'Executive Closing'}</h4>
          <p className="text-slate-700 text-xs leading-relaxed mb-4">
            {language === 'pt' 
              ? 'A rubrica de custos com pessoal apresentou um desvio moderado face ao orçamento e um crescimento expressivo face ao período homólogo. O desvio foi explicado principalmente por maior headcount, com impacto directo em massa salarial, encargos e benefícios. A estrutura manteve predominância operacional directa, mas com crescimento relevante também em áreas de suporte. A produtividade por colaborador melhorou, embora sem compensar integralmente o aumento do custo por unidade operacional. Em termos de leitura financeira, o ponto principal é distinguir o que correspondeu a escala necessária da operação do que passou a representar pressão estrutural adicional.'
              : 'Personnel costs showed moderate budget deviation and significant growth vs prior year. Deviation was mainly explained by higher headcount, with direct impact on payroll, charges and benefits. Structure maintained direct operational predominance, but with relevant growth in support areas. Productivity per employee improved, although not fully offsetting the increase in cost per operational unit. The main point is distinguishing what corresponded to necessary operational scale from what became additional structural pressure.'}
          </p>
        </div>
      </ExecutiveSummarySection>

      {/* Monthly Cost + Headcount Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Custo Mensal e Colaboradores' : 'Monthly Cost & Headcount'}
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={personnelData.monthlyData.map((m, idx) => ({
            ...m,
            cost2024: prevPersonnelData?.monthlyData?.[idx]?.cost || 0,
            headcount2024: prevPersonnelData?.monthlyData?.[idx]?.headcount || 0,
          }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#94A3B8" tickFormatter={(value) => translateMonth(value, language)} />
            <YAxis yAxisId="left" tick={{ fontSize: 10 }} stroke="#94A3B8" domain={[0, 'auto']} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} stroke="#3B82F6" domain={[1600, 2100]} />
            <Tooltip 
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="bg-slate-800 text-white px-3 py-2 rounded-lg shadow-lg text-sm">
                      <p className="font-medium mb-1">{translateMonth(label, language)}</p>
                      <p>{year}: <span className="font-semibold text-red-400">{payload[0]?.value}M USD</span></p>
                      <p>{year - 1}: <span className="text-slate-400">{payload[1]?.value}M USD</span></p>
                      <p>{language === 'pt' ? 'Colaboradores' : 'Headcount'} {year}: <span className="text-blue-400">{payload[2]?.value?.toLocaleString()}</span></p>
                      <p>{language === 'pt' ? 'Colaboradores' : 'Headcount'} {year - 1}: <span className="text-blue-300">{payload[3]?.value?.toLocaleString()}</span></p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar yAxisId="left" dataKey="cost" fill="#0A192F" radius={[4, 4, 0, 0]} name={`${year}`} />
            <Bar yAxisId="left" dataKey="cost2024" fill="#94A3B8" radius={[4, 4, 0, 0]} name={`${year - 1}`} />
            <Line yAxisId="right" type="monotone" dataKey="headcount" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6', r: 3 }} name={language === 'pt' ? `Colaboradores ${year}` : `Headcount ${year}`} />
            <Line yAxisId="right" type="monotone" dataKey="headcount2024" stroke="#93C5FD" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#93C5FD', r: 2 }} name={language === 'pt' ? `Colaboradores ${year - 1}` : `Headcount ${year - 1}`} />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="flex justify-center gap-6 mt-3 text-xs">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-primary rounded"></div><span>{year}</span></div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 bg-slate-400 rounded"></div><span>{year - 1}</span></div>
          <div className="flex items-center gap-1.5"><div className="w-4 h-0.5 bg-blue-500"></div><span>{language === 'pt' ? 'Colaboradores' : 'Headcount'} {year}</span></div>
          <div className="flex items-center gap-1.5"><div className="w-4 h-0.5 bg-blue-300 border-dashed"></div><span>{language === 'pt' ? 'Colaboradores' : 'Headcount'} {year - 1}</span></div>
        </div>
      </div>

      {/* Cost per Operational Unit */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Custo com Pessoal por Unidade Operacional' : 'Personnel Cost per Operational Unit'}
        </h3>
        <div className="grid grid-cols-4 gap-6">
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-xs text-slate-500 tracking-wider mb-2">{language === 'pt' ? 'Por Material Movimentado' : 'Per Material Moved'}</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="font-serif text-2xl font-bold text-primary">${personnelData.costPerUnit.perTonMoved}</span>
              <span className="text-xs text-slate-400">/m³</span>
            </div>
            {prevPersonnelData?.costPerUnit?.perTonMoved && (
              <p className={`text-xs mt-1 ${personnelData.costPerUnit.perTonMoved > prevPersonnelData.costPerUnit.perTonMoved ? 'text-red-500' : 'text-emerald-600'}`}>
                {personnelData.costPerUnit.perTonMoved > prevPersonnelData.costPerUnit.perTonMoved ? '+' : ''}
                {((personnelData.costPerUnit.perTonMoved - prevPersonnelData.costPerUnit.perTonMoved) / prevPersonnelData.costPerUnit.perTonMoved * 100).toFixed(1)}% YoY
              </p>
            )}
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-xs text-slate-500 tracking-wider mb-2">{language === 'pt' ? 'Por Minério Tratado' : 'Per Ore Treated'}</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="font-serif text-2xl font-bold text-primary">${personnelData.costPerUnit.perTonProcessed}</span>
              <span className="text-xs text-slate-400">/m³</span>
            </div>
            {prevPersonnelData?.costPerUnit?.perTonProcessed && (
              <p className={`text-xs mt-1 ${personnelData.costPerUnit.perTonProcessed > prevPersonnelData.costPerUnit.perTonProcessed ? 'text-red-500' : 'text-emerald-600'}`}>
                {personnelData.costPerUnit.perTonProcessed > prevPersonnelData.costPerUnit.perTonProcessed ? '+' : ''}
                {((personnelData.costPerUnit.perTonProcessed - prevPersonnelData.costPerUnit.perTonProcessed) / prevPersonnelData.costPerUnit.perTonProcessed * 100).toFixed(1)}% YoY
              </p>
            )}
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-xs text-slate-500 tracking-wider mb-2">{language === 'pt' ? 'Por Quilate Produzidos (Pedras Regulares)' : 'Per Carat Produced (Regular Stones)'}</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="font-serif text-2xl font-bold text-primary">${personnelData.costPerUnit.perCaratProduced}</span>
              <span className="text-xs text-slate-400">/ct</span>
            </div>
            {prevPersonnelData?.costPerUnit?.perCaratProduced && (
              <p className={`text-xs mt-1 ${personnelData.costPerUnit.perCaratProduced > prevPersonnelData.costPerUnit.perCaratProduced ? 'text-red-500' : 'text-emerald-600'}`}>
                {personnelData.costPerUnit.perCaratProduced > prevPersonnelData.costPerUnit.perCaratProduced ? '+' : ''}
                {((personnelData.costPerUnit.perCaratProduced - prevPersonnelData.costPerUnit.perCaratProduced) / prevPersonnelData.costPerUnit.perCaratProduced * 100).toFixed(1)}% YoY
              </p>
            )}
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl">
            <p className="text-xs text-slate-500 tracking-wider mb-2">{language === 'pt' ? 'Por Quilate Vendido (Pedras Regulares)' : 'Per Carat Sold (Regular Stones)'}</p>
            <div className="flex items-baseline justify-center gap-1">
              <span className="font-serif text-2xl font-bold text-primary">${(personnelData.costPerUnit.perCaratProduced * 1.08).toFixed(1)}</span>
              <span className="text-xs text-slate-400">/ct</span>
            </div>
            {prevPersonnelData?.costPerUnit?.perCaratProduced && (
              <p className={`text-xs mt-1 ${personnelData.costPerUnit.perCaratProduced * 1.08 > prevPersonnelData.costPerUnit.perCaratProduced * 1.08 ? 'text-red-500' : 'text-emerald-600'}`}>
                {personnelData.costPerUnit.perCaratProduced * 1.08 > prevPersonnelData.costPerUnit.perCaratProduced * 1.08 ? '+' : ''}
                {((personnelData.costPerUnit.perCaratProduced * 1.08 - prevPersonnelData.costPerUnit.perCaratProduced * 1.08) / (prevPersonnelData.costPerUnit.perCaratProduced * 1.08) * 100).toFixed(1)}% YoY
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Cost by Area and Headcount by Function */}
      <div className="grid grid-cols-2 gap-6 items-stretch">
        {/* LEFT COLUMN: Cost by Area + Cost Composition */}
        <div className="flex flex-col gap-4">
          {/* Cost by Area */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg font-semibold text-primary">
                {language === 'pt' ? 'Custo por Área' : 'Cost by Area'}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setCostAreaCompareMode('yoy')}
                  className="px-3 py-1.5 text-xs font-medium transition-colors rounded-lg"
                  style={{ 
                    backgroundColor: costAreaCompareMode === 'yoy' ? '#0A192F' : '#F1F4F9',
                    color: costAreaCompareMode === 'yoy' ? '#FFFFFF' : '#64748B',
                    border: costAreaCompareMode === 'yoy' ? 'none' : '1px solid #E2E8F0'
                  }}
                >
                  vs {year - 1}
                </button>
                <button
                  onClick={() => setCostAreaCompareMode('budget')}
                  className="px-3 py-1.5 text-xs font-medium transition-colors rounded-lg"
                  style={{ 
                    backgroundColor: costAreaCompareMode === 'budget' ? '#0A192F' : '#F1F4F9',
                    color: costAreaCompareMode === 'budget' ? '#FFFFFF' : '#64748B',
                    border: costAreaCompareMode === 'budget' ? 'none' : '1px solid #E2E8F0'
                  }}
                >
                  vs {language === 'pt' ? 'Orçamento' : 'Budget'} {year}
                </button>
              </div>
            </div>
            <div className="space-y-2 flex-1">
              {[...personnelData.costByArea]
                .sort((a, b) => b.cost - a.cost)
                .map((area, idx) => {
                  const maxCost = Math.max(...personnelData.costByArea.map(a => a.cost));
                  const barWidth = (area.cost / maxCost) * 100;
                  
                  // Previous year data
                  const prevArea = prevPersonnelData?.costByArea?.find(
                    a => a.area[language] === area.area[language]
                  );
                  const yoyChange = prevArea ? ((area.cost - prevArea.cost) / prevArea.cost * 100).toFixed(0) : null;
                  
                  // Budget data (estimate: 97% of actual)
                  const budgetCost = area.cost * 0.97;
                  const budgetChange = ((area.cost - budgetCost) / budgetCost * 100).toFixed(0);
                  
                  // Use the selected comparison mode
                  const changeValue = costAreaCompareMode === 'yoy' ? yoyChange : budgetChange;
                  const comparisonValue = costAreaCompareMode === 'yoy' 
                    ? (prevArea?.cost || '-') 
                    : budgetCost.toFixed(1);
                  
                  return (
                    <div key={idx} className="flex items-center gap-3 py-1">
                      <span className="text-sm font-medium text-slate-700 truncate w-[140px] flex-shrink-0">
                        {area.area[language]}
                      </span>
                      <div className="flex-1 bg-slate-100 rounded-full h-5 relative">
                        <div 
                          className="h-full rounded-full bg-primary transition-all duration-300"
                          style={{ width: `${barWidth}%` }}
                        ></div>
                      </div>
                      <div className="flex items-center gap-1 w-[65px] justify-end flex-shrink-0">
                        <span className="font-bold text-sm text-primary tabular-nums">{area.cost}M</span>
                      </div>
                      <div className="w-[45px] text-right flex-shrink-0">
                        <span className="text-xs text-slate-400 tabular-nums">{comparisonValue}M</span>
                      </div>
                      {changeValue && (
                        <div className="w-[50px] flex-shrink-0 text-right">
                          <span className={`text-xs px-1.5 py-0.5 rounded font-semibold inline-block tabular-nums ${
                            parseFloat(changeValue) <= 0 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-rose-100 text-rose-700'
                          }`}>
                            {parseFloat(changeValue) > 0 ? '+' : ''}{changeValue}%
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            <div className="border-t border-slate-200 pt-3 mt-auto">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-base text-primary">Total</span>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-xs text-slate-400">{year}: </span>
                    <span className="font-serif text-lg font-bold text-primary">{personnelData.totalCost}M</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-slate-400">
                      {costAreaCompareMode === 'yoy' ? `${year - 1}` : (language === 'pt' ? 'Orç.' : 'Bdgt')}: 
                    </span>
                    <span className="font-semibold text-slate-500">
                      {costAreaCompareMode === 'yoy' 
                        ? (prevPersonnelData?.totalCost || '-') 
                        : budgetPersonnelCost.toFixed(1)}M
                    </span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded font-semibold ${
                    (costAreaCompareMode === 'yoy' ? personnelData.yoyChange : parseFloat(personnelVsBudget)) <= 0 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-rose-100 text-rose-700'
                  }`}>
                    {costAreaCompareMode === 'yoy' 
                      ? `${personnelData.yoyChange > 0 ? '+' : ''}${personnelData.yoyChange.toFixed(1)}%`
                      : `${parseFloat(personnelVsBudget) > 0 ? '+' : ''}${personnelVsBudget}%`
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Qualidade do Custo - Cost Quality */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg font-semibold text-primary">
                {language === 'pt' ? 'Qualidade do Custo' : 'Cost Quality'}
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setCostQualityCompareMode('yoy')}
                  className="px-3 py-1.5 text-xs font-medium transition-colors rounded-lg"
                  style={{ 
                    backgroundColor: costQualityCompareMode === 'yoy' ? '#0A192F' : '#F1F4F9',
                    color: costQualityCompareMode === 'yoy' ? '#FFFFFF' : '#64748B',
                    border: costQualityCompareMode === 'yoy' ? 'none' : '1px solid #E2E8F0'
                  }}
                >
                  vs {year - 1}
                </button>
                <button
                  onClick={() => setCostQualityCompareMode('budget')}
                  className="px-3 py-1.5 text-xs font-medium transition-colors rounded-lg"
                  style={{ 
                    backgroundColor: costQualityCompareMode === 'budget' ? '#0A192F' : '#F1F4F9',
                    color: costQualityCompareMode === 'budget' ? '#FFFFFF' : '#64748B',
                    border: costQualityCompareMode === 'budget' ? 'none' : '1px solid #E2E8F0'
                  }}
                >
                  vs {language === 'pt' ? 'Orçamento' : 'Budget'} {year}
                </button>
              </div>
            </div>
            
            <p className="text-xs text-slate-500 mb-4">{language === 'pt' ? 'Qualidade da Estrutura de Pessoal' : 'Personnel Structure Quality'}</p>
            
            <div className="space-y-4 flex-1">
              {/* Fixo vs Variável */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">{language === 'pt' ? 'Fixo vs Variável' : 'Fixed vs Variable'}</span>
                  <div className="flex items-center gap-2">
                    {costQualityCompareMode === 'yoy' ? (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${75 >= 73 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {75 >= 73 ? '+' : ''}{(75 - 73).toFixed(0)}pp
                      </span>
                    ) : (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${75 >= 74 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {75 >= 74 ? '+' : ''}{(75 - 74).toFixed(0)}pp
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex h-6 rounded-full overflow-hidden">
                  <div 
                    className="bg-blue-500 flex items-center justify-center text-white text-xs font-semibold transition-all"
                    style={{ width: '75%' }}
                  >
                    75% {language === 'pt' ? 'Fixo' : 'Fixed'}
                  </div>
                  <div 
                    className="bg-amber-400 flex items-center justify-center text-slate-800 text-xs font-semibold transition-all"
                    style={{ width: '25%' }}
                  >
                    25% {language === 'pt' ? 'Var.' : 'Var.'}
                  </div>
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>{costQualityCompareMode === 'yoy' ? `${year - 1}: 73% / 27%` : `${language === 'pt' ? 'Orç.' : 'Bdgt'}: 74% / 26%`}</span>
                </div>
              </div>
              
              {/* Directo vs Indirecto */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">{language === 'pt' ? 'Directo vs Indirecto' : 'Direct vs Indirect'}</span>
                  <div className="flex items-center gap-2">
                    {costQualityCompareMode === 'yoy' ? (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${personnelData.directVsIndirect.direct.percentage >= 75 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {personnelData.directVsIndirect.direct.percentage >= 75 ? '+' : ''}{(personnelData.directVsIndirect.direct.percentage - 75).toFixed(1)}pp
                      </span>
                    ) : (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${personnelData.directVsIndirect.direct.percentage >= 78 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {personnelData.directVsIndirect.direct.percentage >= 78 ? '+' : ''}{(personnelData.directVsIndirect.direct.percentage - 78).toFixed(1)}pp
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex h-6 rounded-full overflow-hidden">
                  <div 
                    className="bg-emerald-500 flex items-center justify-center text-white text-xs font-semibold transition-all"
                    style={{ width: `${personnelData.directVsIndirect.direct.percentage}%` }}
                  >
                    {personnelData.directVsIndirect.direct.percentage}% {language === 'pt' ? 'Dir.' : 'Dir.'}
                  </div>
                  <div 
                    className="bg-slate-400 flex items-center justify-center text-white text-xs font-semibold transition-all"
                    style={{ width: `${personnelData.directVsIndirect.indirect.percentage}%` }}
                  >
                    {personnelData.directVsIndirect.indirect.percentage}% {language === 'pt' ? 'Ind.' : 'Ind.'}
                  </div>
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>{costQualityCompareMode === 'yoy' ? `${year - 1}: 75.0% / 25.0%` : `${language === 'pt' ? 'Orç.' : 'Bdgt'}: 78.0% / 22.0%`}</span>
                </div>
              </div>
              
              {/* Base Salarial + Encargos */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">{language === 'pt' ? 'Base Salarial + Encargos' : 'Payroll + Charges'}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-sm text-primary">50.6M</span>
                    <span className="text-xs text-slate-400">(77.8%)</span>
                    {costQualityCompareMode === 'yoy' ? (
                      <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700">+19.5%</span>
                    ) : (
                      <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700">+2.8%</span>
                    )}
                  </div>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div className="bg-blue-600 h-3 rounded-full transition-all" style={{ width: '77.8%' }}></div>
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>{costQualityCompareMode === 'yoy' ? `${year - 1}: 42.3M (78.1%)` : `${language === 'pt' ? 'Orç.' : 'Bdgt'}: 49.2M (78.0%)`}</span>
                </div>
              </div>
              
              {/* Benefícios e Protecção Social */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">{language === 'pt' ? 'Benefícios e Protecção Social' : 'Benefits & Social Protection'}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-sm text-primary">10.4M</span>
                    <span className="text-xs text-slate-400">(16.0%)</span>
                    {costQualityCompareMode === 'yoy' ? (
                      <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700">+20.9%</span>
                    ) : (
                      <span className="text-xs px-1.5 py-0.5 rounded font-semibold bg-rose-100 text-rose-700">+3.0%</span>
                    )}
                  </div>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div className="bg-emerald-500 h-3 rounded-full transition-all" style={{ width: '16%' }}></div>
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>{costQualityCompareMode === 'yoy' ? `${year - 1}: 8.6M (15.9%)` : `${language === 'pt' ? 'Orç.' : 'Bdgt'}: 10.1M (16.0%)`}</span>
                </div>
              </div>
              
              {/* Overtime no Custo Total */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">{language === 'pt' ? 'Overtime no Custo Total' : 'Overtime in Total Cost'}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-sm text-primary">{personnelData.overtimeRatio}%</span>
                    {costQualityCompareMode === 'yoy' ? (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${personnelData.overtimeRatio <= 10.5 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {personnelData.overtimeRatio > 10.5 ? '+' : ''}{(personnelData.overtimeRatio - 10.5).toFixed(1)}pp
                      </span>
                    ) : (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${personnelData.overtimeRatio <= 10.0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                        {personnelData.overtimeRatio > 10.0 ? '+' : ''}{(personnelData.overtimeRatio - 10.0).toFixed(1)}pp
                      </span>
                    )}
                  </div>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3">
                  <div className="bg-amber-500 h-3 rounded-full transition-all" style={{ width: `${personnelData.overtimeRatio * 5}%` }}></div>
                </div>
                <div className="flex justify-between text-xs text-slate-400 mt-1">
                  <span>{costQualityCompareMode === 'yoy' ? `${year - 1}: 10.5%` : `${language === 'pt' ? 'Orç.' : 'Bdgt'}: 10.0%`}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: Headcount by Function */}
        <div className="flex flex-col">
          <div className="bg-white rounded-xl border border-slate-200 p-5 flex-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-lg font-semibold text-primary">
                {language === 'pt' ? 'Colaboradores por Função' : 'Headcount by Function'}
              </h3>
              <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">
                {language === 'pt' ? 'Variação vs Homólogo' : 'YoY Change'}
              </span>
            </div>
            <div className="space-y-2 flex-1">
              {[...personnelData.headcountByFunction]
                .sort((a, b) => b.count - a.count)
                .map((func, idx) => {
                  const maxCount = Math.max(...personnelData.headcountByFunction.map(f => f.count));
                  const barWidth = (func.count / maxCount) * 100;
                  const prevFunc = prevPersonnelData?.headcountByFunction?.find(
                    f => f.function[language] === func.function[language]
                  );
                  const yoyChange = prevFunc ? ((func.count - prevFunc.count) / prevFunc.count * 100).toFixed(0) : null;
                  
                  return (
                    <div key={idx} className="flex items-center gap-3 py-1">
                      <span className="text-sm font-medium text-slate-700 truncate w-[160px] flex-shrink-0">
                        {func.function[language]}
                      </span>
                      <div className="flex-1 bg-slate-100 rounded-full h-5 relative">
                        <div 
                          className="h-full rounded-full bg-blue-500 transition-all duration-300"
                          style={{ width: `${barWidth}%` }}
                        ></div>
                      </div>
                      <div className="flex items-center gap-2 w-[100px] justify-end flex-shrink-0">
                        <span className="font-bold text-sm text-primary tabular-nums text-right w-[40px]">{func.count}</span>
                        <span className="text-xs text-slate-400 tabular-nums text-right w-[50px]">({func.percentage}%)</span>
                      </div>
                      <div className="w-[55px] flex-shrink-0 text-right">
                        {yoyChange && (
                          <span className={`text-xs px-1.5 py-0.5 rounded font-semibold inline-block tabular-nums ${
                            parseFloat(yoyChange) >= 0 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-rose-100 text-rose-700'
                          }`}>
                            {parseFloat(yoyChange) >= 0 ? '+' : ''}{yoyChange}%
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="border-t border-slate-200 pt-3 mt-auto">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-base text-primary">{language === 'pt' ? 'Total colaboradores' : 'Total employees'}</span>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <span className="text-xs text-slate-400">{year}: </span>
                    <span className="font-serif text-lg font-bold text-blue-600">{personnelData.headcount}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-slate-400">{year - 1}: </span>
                    <span className="font-semibold text-slate-500">{prevPersonnelData?.headcount || '-'}</span>
                  </div>
                  {prevPersonnelData?.headcount && (
                    <span className={`text-xs px-2 py-1 rounded font-semibold ${
                      personnelData.headcount >= prevPersonnelData.headcount 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-rose-100 text-rose-700'
                    }`}>
                      {personnelData.headcount >= prevPersonnelData.headcount ? '+' : ''}
                      {((personnelData.headcount - prevPersonnelData.headcount) / prevPersonnelData.headcount * 100).toFixed(1)}% YoY
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Decomposição por Categoria - Category Breakdown Table */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="font-serif text-lg font-semibold text-primary mb-4">
          {language === 'pt' ? 'Decomposição por Categoria' : 'Category Breakdown'}
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0A192F] text-white">
                <th className="text-left py-3 px-3 font-semibold text-xs uppercase tracking-wider">{language === 'pt' ? 'Descrição' : 'Description'}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider">{year}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider">{language === 'pt' ? 'Orç.' : 'Bdgt'}<br/>{year}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-gold-400">{language === 'pt' ? 'Var. Abs.' : 'Var. Abs.'}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-gold-400">{language === 'pt' ? 'Var. %' : 'Var. %'}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-400">{year - 1}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider text-slate-400">{language === 'pt' ? 'Var. % YoY' : 'Var. % YoY'}</th>
                <th className="text-right py-3 px-3 font-semibold text-xs uppercase tracking-wider">% Total</th>
              </tr>
            </thead>
            <tbody>
              {personnelData.costComposition.map((item, idx) => {
                const name = item.component[language];
                // Get previous year data for this component
                const prevYearComp = prevPersonnelData?.costComposition?.find(c => 
                  c.component[language] === name
                );
                const prevYearValue = prevYearComp?.value || (item.value * 0.85);
                // Budget estimation (typically 2-5% variance from actual)
                const budgetValue = item.value * 0.97;
                const varAbs = item.value - budgetValue;
                const varPct = budgetValue !== 0 ? ((item.value - budgetValue) / budgetValue * 100).toFixed(1) : 0;
                const yoyChange = prevYearValue !== 0 ? ((item.value - prevYearValue) / prevYearValue * 100).toFixed(1) : 0;
                
                const colors = ['#0A192F', '#2563EB', '#C5A059', '#F59E0B', '#10B981', '#8B5CF6', '#F43F5E', '#64748B'];
                
                return (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: colors[idx % colors.length] }}></div>
                        <span className="font-medium text-primary">{name}</span>
                      </div>
                    </td>
                    <td className="text-right py-2.5 px-3 font-semibold tabular-nums">{item.value.toFixed(1)}</td>
                    <td className="text-right py-2.5 px-3 text-slate-500 tabular-nums">{budgetValue.toFixed(1)}</td>
                    <td className="text-right py-2.5 px-3 tabular-nums">
                      <span className={varAbs >= 0 ? 'text-red-600' : 'text-emerald-600'}>
                        {varAbs >= 0 ? '+' : ''}{varAbs.toFixed(1)}
                      </span>
                    </td>
                    <td className="text-right py-2.5 px-3 tabular-nums">
                      <span className={parseFloat(varPct) >= 0 ? 'text-red-600' : 'text-emerald-600'}>
                        {parseFloat(varPct) >= 0 ? '+' : ''}{varPct}%
                      </span>
                    </td>
                    <td className="text-right py-2.5 px-3 text-slate-400 tabular-nums">{prevYearValue.toFixed(1)}</td>
                    <td className={`text-right py-2.5 px-3 tabular-nums ${parseFloat(yoyChange) < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {parseFloat(yoyChange) >= 0 ? '+' : ''}{yoyChange}%
                    </td>
                    <td className="text-right py-2.5 px-3 text-slate-600 tabular-nums">{item.percentage.toFixed(1)}%</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gold-50/50 font-semibold">
                <td className="py-3 px-3 text-primary">Total</td>
                <td className="text-right py-3 px-3 tabular-nums">{personnelData.totalCost.toFixed(1)}</td>
                <td className="text-right py-3 px-3 text-slate-600 tabular-nums">{(personnelData.totalCost * 0.97).toFixed(1)}</td>
                <td className="text-right py-3 px-3 tabular-nums">
                  <span className="text-red-600">+{(personnelData.totalCost * 0.03).toFixed(1)}</span>
                </td>
                <td className="text-right py-3 px-3 tabular-nums">
                  <span className="text-red-600">+{personnelVsBudget}%</span>
                </td>
                <td className="text-right py-3 px-3 text-slate-400 tabular-nums">{prevPersonnelData?.totalCost?.toFixed(1) || '-'}</td>
                <td className={`text-right py-3 px-3 tabular-nums ${personnelData.yoyChange < 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {personnelData.yoyChange > 0 ? '+' : ''}{personnelData.yoyChange.toFixed(1)}%
                </td>
                <td className="text-right py-3 px-3 tabular-nums">100%</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PersonnelCosts;
